import apiClient from './apiClient';
import { Address } from 'cluster';
import { TimeSlot, BookingData, BookingResponse } from '../types/Login';


// Address related APIs
export const getAddresses = async (userId: string): Promise<Address[]> => {
  try {
    const response = await apiClient.get(`/addresses`, {
      params: { userId }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching addresses:', error);
    throw new Error('Failed to load addresses');
  }
};

export const saveAddress = async (userId: string, details: string): Promise<Address> => {
  try {
    const response = await apiClient.post(`/addresses`, {
      userId,
      label: 'New Address',
      details
    });
    return response.data;
  } catch (error) {
    console.error('Error saving address:', error);
    throw new Error('Failed to save address');
  }
};

// Booking related APIs
export const getAvailableTimeSlots = async (date: string, duration: number): Promise<TimeSlot[]> => {
  try {
    const response = await apiClient.get(`/timeslots`, {
      params: { date, duration }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching time slots:', error);
    throw new Error('Failed to load available time slots');
  }
};

export const createBooking = async (bookingData: BookingData): Promise<BookingResponse> => {
  const response = await apiClient.post(`/order-management`, bookingData);
  return response.data;
};

/** Extract order_id from POST /order-management response (id, order_id, or data.order_id) */
export function getOrderIdFromBookingResponse(res: BookingResponse | { data?: BookingResponse }): string | null {
  const top = res as Record<string, unknown>;
  if (top?.success === false) return null;
  const nested = top?.data;
  const raw =
    nested && typeof nested === 'object' && !Array.isArray(nested)
      ? (nested as Record<string, unknown>)
      : top;
  const id = raw?.order_id ?? raw?.id ?? raw?.orderId;
  return id != null && String(id).trim() !== '' ? String(id) : null;
}

/** Payment details payload sent to booking API after PhonePe payment success */
export interface PaymentDetailsForBooking {
  merchantId: string;
  merchantOrderId: string;
  orderId: string;
  amount: number;
  feeAmount: number | null;
  payableAmount: number;
  state: string;
  isRedirectionEnabled: boolean;
  isRetryEnabled: boolean;
}

export const postPaymentDetailsToBooking = async (payload: PaymentDetailsForBooking) => {
  const response = await apiClient.post(`/order-management/payment`, payload);
  return response.data;
};

/**
 * Optional: ask server for payment status (e.g. after webhook may have updated).
 * Backend can implement GET /payment-transaction/status?merchantOrderId=... or ?transactionId=...
 * Returns { status: 'COMPLETED' | 'PENDING' | 'FAILED', ... } so UI can reconcile.
 */
export const getPaymentStatus = async (params: { merchantOrderId?: string; transactionId?: string }) => {
  const search = new URLSearchParams();
  if (params.merchantOrderId) search.set('merchantOrderId', params.merchantOrderId);
  if (params.transactionId) search.set('transactionId', params.transactionId);
  const response = await apiClient.get(`/payment-transaction/status?${search.toString()}`);
  return response.data;
};

/**
 * Call payment-status API after payment (redirect or iframe). Payload is merchant order id.
 */
export const postPaymentStatus = async (merchantOrderId: string) => {
  const response = await apiClient.post('/payment-transaction/payment-status', { merchantOrderId });
  return response.data;
};


// Normalize subcategory item; preserve serivce_pricing and service_about for UI
export interface SubcategoryItem {
  id: string;
  name: string;
  service_name?: string;
  serivce_pricing?: string | number;
  service_about?: string;
  [key: string]: unknown;
}

function toSubcategory(item: any): SubcategoryItem {
  return {
    id: String(item?.id ?? ''),
    name: String(item?.name ?? item?.service_name ?? item?.title ?? ''),
    service_name: item?.service_name ?? item?.name,
    serivce_pricing: item?.serivce_pricing ?? item?.service_pricing,
    service_about: item?.service_about ?? item?.service_description ?? item?.description,
  };
}

// Parse array from various response shapes (array or single object from API)
function parseSubcategoryArray(raw: any): any[] {
  if (Array.isArray(raw)) return raw;
  if (raw?.data && Array.isArray(raw.data)) return raw.data;
  if (raw?.data && typeof raw.data === 'object' && !Array.isArray(raw.data)) return [raw.data];
  if (raw?.subcategories && Array.isArray(raw.subcategories)) return raw.subcategories;
  if (raw?.items && Array.isArray(raw.items)) return raw.items;
  if (raw && typeof raw === 'object' && (raw.id || raw.service_name)) return [raw];
  return [];
}

export const getSubcategories = async (
  id: string,
  _categoryName?: string
): Promise<SubcategoryItem[]> => {
  const categoryId = String(id || '').trim();
  if (!categoryId) return [];
  try {
    const res = await apiClient.get(`/services/fetchByService_Catageory/${categoryId}`);
    const data = res.data?.data ?? res.data;
    const arr = parseSubcategoryArray(data);
    if (Array.isArray(arr) && arr.length > 0) {
      return arr.map(toSubcategory).filter((s) => s.id && s.name);
    }
  } catch {
    // API failed - return empty
  }
  return [];
};

export const getServices = async (id: string) => {
  try {
    const res = await apiClient.get(`/services/${id}`);
    const data = res.data?.data ?? res.data;
    return Array.isArray(data) ? data : [];
  } catch {
    return [];
  }
};